// frontend/src/rma/components/TriageLine.jsx
import React, { useState, useCallback, useId } from 'react'
import { Box, Typography, IconButton, Collapse } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { FS } from '../../theme/tokens'

/**
 * TriageLine — one-line, clamped "why is this case ranked here" row.
 *
 * @description
 *   Renders the deterministic ranking reason produced by
 *   `rma/lib/triageReason.js` directly under a case row's primary line. One
 *   pattern for BOTH breakpoints (Owner decision Q7 = C, 2026-08-06): clamped to
 *   a single line, expandable via a chevron — rather than desktop-always /
 *   mobile-hover, which would have made "ranking is never a black box" true only
 *   where a pointer exists.
 *
 *   🔴 **The contract is: visible where it DISTINGUISHES, not on every row.**
 *   (7C refined by the owner 2026-08-07.) `triageReason` returns null for the
 *   `recency` tail — that branch only restates the sort rule, so it would be
 *   true of the whole lower half of every page and distinguish nothing.
 *   `no_activity` DOES render: "nobody outside has ever touched this" is a
 *   state, not a blank. So: **if a row has a line, that row is here for a
 *   reason.** Do not "fill the gaps" by giving `recency` a string.
 *
 *   🔴 This is a RULE restated in words, never agent output. It must not use
 *   agent-violet, must not carry a TierBadge, and must not state a confidence.
 *   The visual language is deliberately quiet — `ink-70`, `FS.xs`, no fill —
 *   so it cannot be mistaken for the (not yet buildable) recommendation chip.
 *
 * @useWhen
 *   - A list row needs to explain its own position in a server-side ranking
 *   - Any dense row where the justification must survive on touch
 *
 * @dontUseFor
 *   - Agent / LLM recommendations — those need provenance + tier, not this
 *   - Status or state display — use Pill / StatusChip
 *   - Multi-paragraph explanations — this is one clamped line by contract
 *
 * @related
 *   - rma/lib/triageReason.js — the pure function that produces the text
 *   - Pill — for state chips, the visually louder sibling
 *   - ProvenanceMark — for CLAIMED facts; this line states a rule, not a claim
 *
 * @reuseHints
 *   - PO list once recommended-reorder ranking becomes explainable
 *   - Discovery candidate list (why this candidate scored where it did)
 *
 * @props
 *   reason   — {key, text, tone, detail} from triageReason(), or null → renders nothing
 *   detail   — longer string revealed by the chevron. Callers pass
 *              `reason?.detail`; without it NO chevron renders at all.
 *   dense    — tighter metrics for the desktop table row
 *
 */
export default function TriageLine({ reason, detail, dense = false }) {
  const [open, setOpen] = useState(false)
  const regionId = useId()

  const toggle = useCallback((e) => {
    // The whole row navigates to the case — expanding the reason must not.
    e.stopPropagation()
    setOpen(v => !v)
  }, [])

  // Absence is a statement: no reason ⇒ no line. Callers pass null when the
  // user has taken over sorting, because then the server ranking this line
  // would explain is no longer the order on screen.
  if (!reason) return null

  const toneColor = {
    warn: 'warning.main',
    err: 'error.main',
    ok: 'success.main',
    info: 'info.main',
    plain: 'text.secondary',
  }[reason.tone] || 'text.secondary'

  return (
    <Box sx={{ width: '100%' }}>
      {/* N2: minHeight 32 reserviert die Hoehe der Trefferflaeche IN der Zeile,
          statt sie nach oben in den Produkttitel ragen zu lassen. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, minHeight: 32 }}>
        <Box
          aria-hidden
          sx={{
            width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
            backgroundColor: toneColor, opacity: 0.75,
          }}
        />
        <Typography
          component="span"
          data-testid="triage-reason"
          data-reason-key={reason.key}
          sx={{
            fontSize: FS.xs,
            color: 'text.secondary',
            minWidth: 0, flex: 1,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 1,
            overflow: 'hidden',
            lineHeight: dense ? 1.35 : 1.45,
          }}
        >
          {reason.text}
        </Typography>
        {detail && (
          <IconButton
            size="small"
            onClick={toggle}
            aria-expanded={open}
            aria-controls={regionId}
            aria-label={open ? 'Hide ranking detail' : 'Show ranking detail'}
            sx={{
              // 🔴 Die Trefferflaeche darf NICHT ueber die Zeile hinausragen.
              // Vorher: 44x44 zentriert auf einer 20px-Schaltflaeche, also 12px
              // Ueberstand oben und unten — und der Produkttitel sitzt 2px
              // darueber. Ein Tipp auf das Ende eines langen Produktnamens
              // haette die Begruendung aufgeklappt statt den Fall zu oeffnen,
              // weil `toggle` stopPropagation ruft. Auf mobil 6px Ueberlappung.
              // Jetzt: die Schaltflaeche IST 32px hoch, die Flexzeile
              // reserviert dieselben 32px (minHeight oben), der Ueberstand ist
              // horizontal — dort steht nichts Klickbares.
              width: 32, height: 32, p: 0, flexShrink: 0, position: 'relative',
              '&::after': {
                content: '""', position: 'absolute',
                top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: 44, height: '100%',
              },
              '& svg': {
                fontSize: 16,
                transition: 'transform 180ms ease',   // 150-200ms house rule
                transform: open ? 'rotate(180deg)' : 'none',
              },
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        )}
      </Box>
      {detail && (
        // `id` sits on the ALWAYS-MOUNTED wrapper, not inside the Collapse:
        // with `unmountOnExit` the inner node is absent while collapsed, so
        // aria-controls would point at nothing (dangling IDREF, WCAG 4.1.2).
        <Box id={regionId}>
          <Collapse in={open} timeout={180} unmountOnExit>
            <Typography sx={{ fontSize: FS.xs, color: 'text.secondary', pl: 1.5, pt: 0.25 }}>
              {detail}
            </Typography>
          </Collapse>
        </Box>
      )}
    </Box>
  )
}
